/**
 * Memory-optimized NodeDto using string interning.
 *
 * String interning replaces duplicate string values with indices
 * pointing to a shared StringStorage. This significantly reduces
 * memory usage when loading large node arrays (100k+ nodes) where
 * fields like cadFamily, cadMaterial, etc. contain many duplicates.
 *
 * Interned fields store numeric indices internally but expose
 * string values via getters/setters, making this class fully
 * transparent to existing code that expects NodeDto.
 *
 * @example
 * ```typescript
 * const storage = new StringStorage();
 * InternedNodeDto.setStorage(storage);
 *
 * const node = new InternedNodeDto();
 * node.cadFamily = "Basic Wall";  // Setter interns the string
 * node.cadFamily;                  // Getter returns "Basic Wall"
 * ```
 */

import type { ModelParameterDto } from '@memorypack/ModelParameterDto';
import type { INodeDto } from './INodeDto';
import { StringStorage } from './StringStorage';

/**
 * List of fields that use string interning.
 * These fields store indices internally and resolve to strings via getters.
 */
export const INTERNED_FIELDS = [
  'cadId',
  'cadFamily',
  'cadName',
  'cadMaterial',
  'cadReferenceId',
  'omniClassNumber',
  'omniClassTitle',
  'uniFormatNumber',
  'uniFormatTitle',
  'masterFormatNumber',
  'masterFormatTitle',
  'multiplierInput',
  'notes',
  'carbonImpactInput',
  'carbonImpactNotes',
  'freeText1',
  'freeText2',
  'freeText3',
] as const;

export type InternedFieldName = (typeof INTERNED_FIELDS)[number];

export class InternedNodeDto implements INodeDto {
  // ============================================================================
  // Static configuration and storage reference (shared across all instances)
  // ============================================================================

  private static storage: StringStorage | null = null;

  /**
   * When true, string interning is enabled and string fields are stored as indices.
   * When false, strings are stored directly (no memory optimization, useful for testing).
   * @default true
   */
  private static _interningEnabled: boolean = true;

  /**
   * Gets whether string interning is enabled.
   */
  static get interningEnabled(): boolean {
    return InternedNodeDto._interningEnabled;
  }

  /**
   * Enables or disables string interning.
   * When disabled, strings are stored directly without deduplication.
   * This is useful for A/B testing memory usage with/without interning.
   * 
   * @param enabled - Whether to enable string interning
   */
  static setInterningEnabled(enabled: boolean): void {
    InternedNodeDto._interningEnabled = enabled;
    console.log(`[InternedNodeDto] String interning ${enabled ? 'ENABLED' : 'DISABLED'}`);
  }

  /**
   * Sets the shared StringStorage instance for all InternedNodeDto instances.
   * Must be called before creating or using any InternedNodeDto instances.
   *
   * @param storage - The StringStorage instance to use for interning
   */
  static setStorage(storage: StringStorage): void {
    InternedNodeDto.storage = storage;
  }

  /**
   * Gets the current StringStorage instance.
   * Throws if storage has not been set and interning is enabled.
   */
  private static getStorage(): StringStorage {
    if (!InternedNodeDto.storage) {
      throw new Error('InternedNodeDto.setStorage() must be called before using InternedNodeDto');
    }
    return InternedNodeDto.storage;
  }

  /**
   * Clears the static storage reference.
   * Call this after processing is complete to allow garbage collection.
   */
  static clearStorage(): void {
    InternedNodeDto.storage = null;
  }

  // ============================================================================
  // Non-interned fields (stored directly)
  // ============================================================================

  nodeId: bigint = 0n;
  nodePath: string | null = null;
  nodeType: number = 0;
  modelId: string = '00000000-0000-0000-0000-000000000000';
  cadModelCategoryId: string = '00000000-0000-0000-0000-000000000000';
  isStructuralMaterial: boolean | null = null;
  scopeId: number | null = null;
  componentCount: number = 0;
  quantity: number | null = null;
  length: number | null = null;
  area: number | null = null;
  volume: number | null = null;
  thickness: number | null = null;
  originalQuantity: number | null = null;
  originalLength: number | null = null;
  originalArea: number | null = null;
  originalVolume: number | null = null;
  originalThickness: number | null = null;
  multiplierValue: number | null = null;
  carbonImpactValue: number | null = null;
  hasChildCarbonImpactOverride: boolean = false;
  systemId: string | null = null;
  subSystemId: string | null = null;
  co2MaterialTypeSpec: string | null = null;
  modelParameters: (ModelParameterDto | null)[] | null = null;

  // ============================================================================
  // Interned fields (stored as indices when interning enabled, or strings when disabled)
  // ============================================================================

  // When interning is enabled: stores numeric index into StringStorage
  // When interning is disabled: stores the string directly (cast to number for union type)
  private _cadId: number | string | null = 0;
  private _cadFamily: number | string | null = 0;
  private _cadName: number | string | null = 0;
  private _cadMaterial: number | string | null = 0;
  private _cadReferenceId: number | string | null = 0;
  private _omniClassNumber: number | string | null = 0;
  private _omniClassTitle: number | string | null = 0;
  private _uniFormatNumber: number | string | null = 0;
  private _uniFormatTitle: number | string | null = 0;
  private _masterFormatNumber: number | string | null = 0;
  private _masterFormatTitle: number | string | null = 0;
  private _multiplierInput: number | string | null = 0;
  private _notes: number | string | null = 0;
  private _carbonImpactInput: number | string | null = 0;
  private _carbonImpactNotes: number | string | null = 0;
  private _freeText1: number | string | null = 0;
  private _freeText2: number | string | null = 0;
  private _freeText3: number | string | null = 0;

  // ============================================================================
  // Getters and Setters for interned fields
  // When interning is disabled, strings are stored directly.
  // ============================================================================

  get cadId(): string | null {
    if (!InternedNodeDto._interningEnabled) return this._cadId as string | null;
    return InternedNodeDto.getStorage().get(this._cadId as number);
  }
  set cadId(value: string | null) {
    if (!InternedNodeDto._interningEnabled) { this._cadId = value; return; }
    this._cadId = InternedNodeDto.getStorage().add(value);
  }

  get cadFamily(): string | null {
    if (!InternedNodeDto._interningEnabled) return this._cadFamily as string | null;
    return InternedNodeDto.getStorage().get(this._cadFamily as number);
  }
  set cadFamily(value: string | null) {
    if (!InternedNodeDto._interningEnabled) { this._cadFamily = value; return; }
    this._cadFamily = InternedNodeDto.getStorage().add(value);
  }

  get cadName(): string | null {
    if (!InternedNodeDto._interningEnabled) return this._cadName as string | null;
    return InternedNodeDto.getStorage().get(this._cadName as number);
  }
  set cadName(value: string | null) {
    if (!InternedNodeDto._interningEnabled) { this._cadName = value; return; }
    this._cadName = InternedNodeDto.getStorage().add(value);
  }

  get cadMaterial(): string | null {
    if (!InternedNodeDto._interningEnabled) return this._cadMaterial as string | null;
    return InternedNodeDto.getStorage().get(this._cadMaterial as number);
  }
  set cadMaterial(value: string | null) {
    if (!InternedNodeDto._interningEnabled) { this._cadMaterial = value; return; }
    this._cadMaterial = InternedNodeDto.getStorage().add(value);
  }

  get cadReferenceId(): string | null {
    if (!InternedNodeDto._interningEnabled) return this._cadReferenceId as string | null;
    return InternedNodeDto.getStorage().get(this._cadReferenceId as number);
  }
  set cadReferenceId(value: string | null) {
    if (!InternedNodeDto._interningEnabled) { this._cadReferenceId = value; return; }
    this._cadReferenceId = InternedNodeDto.getStorage().add(value);
  }

  get omniClassNumber(): string | null {
    if (!InternedNodeDto._interningEnabled) return this._omniClassNumber as string | null;
    return InternedNodeDto.getStorage().get(this._omniClassNumber as number);
  }
  set omniClassNumber(value: string | null) {
    if (!InternedNodeDto._interningEnabled) { this._omniClassNumber = value; return; }
    this._omniClassNumber = InternedNodeDto.getStorage().add(value);
  }

  get omniClassTitle(): string | null {
    if (!InternedNodeDto._interningEnabled) return this._omniClassTitle as string | null;
    return InternedNodeDto.getStorage().get(this._omniClassTitle as number);
  }
  set omniClassTitle(value: string | null) {
    if (!InternedNodeDto._interningEnabled) { this._omniClassTitle = value; return; }
    this._omniClassTitle = InternedNodeDto.getStorage().add(value);
  }

  get uniFormatNumber(): string | null {
    if (!InternedNodeDto._interningEnabled) return this._uniFormatNumber as string | null;
    return InternedNodeDto.getStorage().get(this._uniFormatNumber as number);
  }
  set uniFormatNumber(value: string | null) {
    if (!InternedNodeDto._interningEnabled) { this._uniFormatNumber = value; return; }
    this._uniFormatNumber = InternedNodeDto.getStorage().add(value);
  }

  get uniFormatTitle(): string | null {
    if (!InternedNodeDto._interningEnabled) return this._uniFormatTitle as string | null;
    return InternedNodeDto.getStorage().get(this._uniFormatTitle as number);
  }
  set uniFormatTitle(value: string | null) {
    if (!InternedNodeDto._interningEnabled) { this._uniFormatTitle = value; return; }
    this._uniFormatTitle = InternedNodeDto.getStorage().add(value);
  }

  get masterFormatNumber(): string | null {
    if (!InternedNodeDto._interningEnabled) return this._masterFormatNumber as string | null;
    return InternedNodeDto.getStorage().get(this._masterFormatNumber as number);
  }
  set masterFormatNumber(value: string | null) {
    if (!InternedNodeDto._interningEnabled) { this._masterFormatNumber = value; return; }
    this._masterFormatNumber = InternedNodeDto.getStorage().add(value);
  }

  get masterFormatTitle(): string | null {
    if (!InternedNodeDto._interningEnabled) return this._masterFormatTitle as string | null;
    return InternedNodeDto.getStorage().get(this._masterFormatTitle as number);
  }
  set masterFormatTitle(value: string | null) {
    if (!InternedNodeDto._interningEnabled) { this._masterFormatTitle = value; return; }
    this._masterFormatTitle = InternedNodeDto.getStorage().add(value);
  }

  get multiplierInput(): string | null {
    if (!InternedNodeDto._interningEnabled) return this._multiplierInput as string | null;
    return InternedNodeDto.getStorage().get(this._multiplierInput as number);
  }
  set multiplierInput(value: string | null) {
    if (!InternedNodeDto._interningEnabled) { this._multiplierInput = value; return; }
    this._multiplierInput = InternedNodeDto.getStorage().add(value);
  }

  get notes(): string | null {
    if (!InternedNodeDto._interningEnabled) return this._notes as string | null;
    return InternedNodeDto.getStorage().get(this._notes as number);
  }
  set notes(value: string | null) {
    if (!InternedNodeDto._interningEnabled) { this._notes = value; return; }
    this._notes = InternedNodeDto.getStorage().add(value);
  }

  get carbonImpactInput(): string | null {
    if (!InternedNodeDto._interningEnabled) return this._carbonImpactInput as string | null;
    return InternedNodeDto.getStorage().get(this._carbonImpactInput as number);
  }
  set carbonImpactInput(value: string | null) {
    if (!InternedNodeDto._interningEnabled) { this._carbonImpactInput = value; return; }
    this._carbonImpactInput = InternedNodeDto.getStorage().add(value);
  }

  get carbonImpactNotes(): string | null {
    if (!InternedNodeDto._interningEnabled) return this._carbonImpactNotes as string | null;
    return InternedNodeDto.getStorage().get(this._carbonImpactNotes as number);
  }
  set carbonImpactNotes(value: string | null) {
    if (!InternedNodeDto._interningEnabled) { this._carbonImpactNotes = value; return; }
    this._carbonImpactNotes = InternedNodeDto.getStorage().add(value);
  }

  get freeText1(): string | null {
    if (!InternedNodeDto._interningEnabled) return this._freeText1 as string | null;
    return InternedNodeDto.getStorage().get(this._freeText1 as number);
  }
  set freeText1(value: string | null) {
    if (!InternedNodeDto._interningEnabled) { this._freeText1 = value; return; }
    this._freeText1 = InternedNodeDto.getStorage().add(value);
  }

  get freeText2(): string | null {
    if (!InternedNodeDto._interningEnabled) return this._freeText2 as string | null;
    return InternedNodeDto.getStorage().get(this._freeText2 as number);
  }
  set freeText2(value: string | null) {
    if (!InternedNodeDto._interningEnabled) { this._freeText2 = value; return; }
    this._freeText2 = InternedNodeDto.getStorage().add(value);
  }

  get freeText3(): string | null {
    if (!InternedNodeDto._interningEnabled) return this._freeText3 as string | null;
    return InternedNodeDto.getStorage().get(this._freeText3 as number);
  }
  set freeText3(value: string | null) {
    if (!InternedNodeDto._interningEnabled) { this._freeText3 = value; return; }
    this._freeText3 = InternedNodeDto.getStorage().add(value);
  }
}
